/** Helpers deciding whether an upload must skip canvas re-encoding to keep its format/animation. */

/**
 * Compression limits applied to uploads that still need to be re-encoded.
 * @type {{ maxSizeMB: number, maxWidthOrHeight: number, useWebWorker: boolean }}
 */
export const COMPRESSION_OPTIONS = {
  maxSizeMB: 1,
  maxWidthOrHeight: 1920,
  useWebWorker: true,
};

/**
 * @param {File} file - the file to inspect.
 * @returns {boolean} true when the file is an SVG (by MIME type or extension).
 */
export const isSvgFile = (file) =>
  (file.type || '').toLowerCase() === 'image/svg+xml' ||
  file.name.toLowerCase().endsWith('.svg');

/**
 * @param {File} file - the file to inspect.
 * @returns {boolean} true when the file is a GIF (by MIME type or extension).
 */
export const isGifFile = (file) =>
  (file.type || '').toLowerCase() === 'image/gif' ||
  file.name.toLowerCase().endsWith('.gif');

/**
 * @param {File} file - the file to inspect.
 * @returns {boolean} true when the file size is already within the compression limit.
 */
export const isWithinSizeLimit = (file) =>
  file.size <= COMPRESSION_OPTIONS.maxSizeMB * 1024 * 1024;

/**
 * Decides whether an upload must skip canvas compression.
 *
 * GIF and SVG must never be re-encoded (GIF loses animation; SVG is text/XML
 * based). Already-small PNG/JPEG are also passed through untouched so their
 * original format and bytes are preserved.
 *
 * @param {File} file - the file to inspect.
 * @returns {boolean} true when compression should be skipped.
 */
export const shouldSkipCompression = (file) => {
  if (isSvgFile(file) || isGifFile(file)) {
    return true;
  }
  return isWithinSizeLimit(file);
};
